// Share a link to something in the app.
//
// Uses the platform's native share sheet where there is one — iOS, Android,
// and the Capacitor WebView inherits it — and falls back to the clipboard
// everywhere else (most desktop browsers).
//
// Callers get back which path ran so they can decide about feedback: the
// native sheet reports its own outcome, so a toast on top of it is noise,
// while a silent clipboard write needs one. A dismissed sheet is NOT an error
// — the user chose to cancel and should see nothing.
//
// Both paths reject in ordinary use: navigator.share throws AbortError on
// dismissal, and clipboard writes throw without a secure context or
// permission. Neither is worth surfacing as a failure the user caused.

export const SHARE_RESULT = {
  SHARED: 'shared',       // native sheet completed
  DISMISSED: 'dismissed', // native sheet cancelled — say nothing
  COPIED: 'copied',       // clipboard fallback worked — confirm it
  ERROR: 'error',         // clipboard unavailable — say so
};

export async function shareLink({ title, path }) {
  const url = `${window.location.origin}${path}`;

  if (navigator.share) {
    try {
      await navigator.share({ title, url });
      return SHARE_RESULT.SHARED;
    } catch {
      return SHARE_RESULT.DISMISSED;
    }
  }

  try {
    await navigator.clipboard.writeText(url);
    return SHARE_RESULT.COPIED;
  } catch {
    return SHARE_RESULT.ERROR;
  }
}

// The toast half of the decision above, so every caller words it the same way.
export function shareToastMessage(result) {
  if (result === SHARE_RESULT.COPIED) return 'Link copied';
  if (result === SHARE_RESULT.ERROR) return 'Could not copy link.';
  return null; // shared or dismissed — the sheet already spoke
}
